// Datums-Anzeige — EINE Formatierung für das ganze CMS.
//
// ⚠️ Vorher gab es fünf Fassungen, und zwei davon zeigten dem Browser einer Nutzerin mit
// US-Spracheinstellung schlicht das falsche Datum:
//
// | Ort                              | Aufruf                                              | Ergebnis am 5.8.2026 |
// |----------------------------------|-----------------------------------------------------|----------------------|
// | `Dashboard.tsx`                  | `toLocaleDateString()` — OHNE Sprache               | `8/5/2026` (US!)     |
// | `ContentManagement.tsx`          | `toLocaleDateString()` — OHNE Sprache               | `8/5/2026` (US!)     |
// | `Profile.tsx` (2×)               | `toLocaleDateString('de-DE')`                       | `5.8.2026`           |
// | `UserManagement.tsx`             | `Intl.DateTimeFormat('de-DE', { 2-stellig … })`     | `05.08.2026`         |
//
// Die beiden sprachlosen Aufrufe sind kein Schönheitsfehler: `8/5/2026` ist am 5. August ein
// LESBARES, aber FALSCHES Datum — niemandem fällt auf, dass Tag und Monat vertauscht sind. Genau
// deshalb ist `'de-DE'` hier fest verdrahtet und nicht wählbar. Ein CMS mit deutscher Oberfläche
// hat kein Interesse daran, was im Betriebssystem der Betrachterin eingestellt ist.
//
// **Zweistellig gewinnt (`05.08.2026`).** Das ist die Fassung aus `UserManagement.tsx` — die
// jüngste, und die einzige, die in einer Tabellenspalte nicht springt: bei `5.8.` gegen `15.12.`
// wandern die Punkte pro Zeile hin und her. `Profile.tsx` verliert dabei seine ungepolsterte
// Schreibweise; das ist die bewusst in Kauf genommene Angleichung.
//
// **Ein einziges `Intl.DateTimeFormat` auf Modulebene**, nicht pro Aufruf. Das Aufbauen eines
// Formatierers ist der teure Teil; in einer Tabelle mit 20 Zeilen × 2 Datumsspalten wären es sonst
// 40 Neubauten pro Darstellung.

/**
 * Der Formatierer selbst. Modulweit EINE Instanz — siehe Kommentar oben.
 * `2-digit`/`numeric` ergibt `05.08.2026`.
 */
const DATUM = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

/**
 * Standard-Rückfall für „kein Datum vorhanden".
 *
 * ⚠️ Gemessen standen hier drei verschiedene Zeichen: `'–'` (Benutzer-Verwaltung), `'N/A'` und
 * `'Noch nie'` (beide Profil). `'N/A'` fällt ersatzlos weg — es ist englisch, und englische
 * Resttexte haben in dieser Oberfläche nichts verloren. Der Gedankenstrich wird der Standard, weil
 * er in einer Tabellenspalte am wenigsten Lärm macht.
 */
export const KEIN_DATUM = '–';

/**
 * Formatiert einen Datumswert für die Anzeige.
 *
 * @param wert   ISO-Zeichenkette, `Date`, `null` oder `undefined` — alles, was die API liefern kann.
 * @param ersatz Text für den Fall, dass nichts Darstellbares herauskommt.
 *
 * ⚠️ Der `ersatz`-Parameter ist bewusst vorhanden, obwohl geteilte Bausteine sonst möglichst wenig
 * Wahlrecht anbieten sollen (siehe `PageLayout.tsx`). Der Unterschied: hier steht keine Optik zur
 * Wahl, sondern die BEDEUTUNG der Leerstelle. Im Profil trägt das Feld „Letzte Anmeldung" die
 * Aussage `'Noch nie'`, und die ist echte Information — ein Gedankenstrich an derselben Stelle
 * lässt offen, ob nie angemeldet oder nur nicht bekannt. Wer keinen Grund hat, lässt den Parameter
 * weg.
 *
 * Ein unlesbarer Wert ergibt ebenfalls den Rückfall, nicht `Invalid Date`. Ein deutsches
 * „Invalid Date" mitten in einer Tabelle ist schlechter als der Strich, der ohnehin für „nichts"
 * steht — und es ist kein Fehler, den die Nutzerin beheben könnte.
 */
export const datumKurz = (wert?: string | Date | null, ersatz: string = KEIN_DATUM): string => {
  if (!wert) return ersatz;
  const d = wert instanceof Date ? wert : new Date(wert);
  return Number.isNaN(d.getTime()) ? ersatz : DATUM.format(d);
};

/**
 * Derselbe Formatierer mit Uhrzeit — `05.08.2026, 14:32`. Modulweit EINE Instanz, aus demselben
 * Grund wie `DATUM` oben.
 */
const DATUM_ZEIT = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * Datum MIT Uhrzeit. Schwester von `datumKurz`, nicht deren Ersatz.
 *
 * ⚠️ Warum überhaupt eine zweite Funktion — der Anlass ist gemessen, nicht ästhetisch: die
 * Versionsliste (`components/content/Versionsliste.tsx`) zeigt mehrere Fassungen DESSELBEN
 * Inhalts. Ein Inhalt, der an einem Vormittag dreimal gespeichert wird, erzeugt drei Revisionen —
 * mit `datumKurz` stünde in allen drei Zeilen `05.08.2026`, und die Frage „welche Fassung hole ich
 * zurück?" wäre am Bildschirm nicht beantwortbar. Der Zeitpunkt ist dort das
 * UNTERSCHEIDUNGSMERKMAL, nicht Beiwerk.
 *
 * ⚠️ Warum eine Schwester und KEIN Schalter an `datumKurz` (`datumKurz(wert, { zeit: true })`):
 * die Anzeigedauer-Frage steht am Aufrufort, nicht in der Funktion. Ein Flag müsste an jeder der
 * bestehenden Tabellenspalten mitgelesen werden, und die Modulebene trüge dann entweder zwei
 * Formatierer hinter einer Weiche oder — wahrscheinlicher — einen pro Aufruf. Genau das
 * verhindert der Kopf dieser Datei. Zwei benannte Funktionen, zwei feste Formatierer, keine
 * Weiche.
 *
 * ⚠️ Die ZEITZONE ist bewusst die des Browsers, anders als die fest verdrahtete Sprache. Die
 * Sprache ist verdrahtet, weil `8/5/2026` ein falsches Datum ANZEIGT; eine Uhrzeit dagegen ist in
 * der Zone der Betrachterin richtig und in einer fremden falsch. Wer in Deutschland arbeitet,
 * sieht deutsche Ortszeit — ohne dass die Anwendung das erzwingen müsste.
 *
 * Rückfall und Umgang mit unlesbaren Werten wie bei `datumKurz` — siehe dort.
 */
export const datumZeit = (wert?: string | Date | null, ersatz: string = KEIN_DATUM): string => {
  if (!wert) return ersatz;
  const d = wert instanceof Date ? wert : new Date(wert);
  return Number.isNaN(d.getTime()) ? ersatz : DATUM_ZEIT.format(d);
};
